import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import backArrow from "../../assets/images/backArrow.svg";
import { buildSessionPath, getStoredSession } from "../mmtUserList/mmtFlow";
import { getPatientDetails, updatePatient } from "../mmtUserList/mmtApi";
import { USER_LIST } from "../../routes/routerPath";
import { isWebView, postWebViewMessage } from "../../utlis/webViewBridge";
import { getCurrentLocale, pageLocale } from "../../utlis/locale";
import {
  getHeightWeightValidation,
  hydrateHeightWeightFields,
  toStoredHeightWeight,
} from "./measurementUnits";
import {
  digitsOnly,
  getContactFieldRules,
  sanitizeContactValue,
} from "./contactFieldRules";
import HeightWeightFields from "./HeightWeightFields";
import "./testperson.scss";

const GENDERS = ["male", "female", "other"];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 80 }, (_, i) => currentYear - 21 - i);

const EXERCISES = ["low", "moderate", "good"];

const INJURIES = [
  "knee",
  "hip",
  "heart",
  "shoulder",
  "ankle",
  "eye",
  "arm",
  "hand_wrist",
  "stomach_intestine",
  "lungs",
  "other",
];

const MEDICATIONS = [
  "antiseizure",
  "diabetes",
  "pain_muscle",
  "mood_psych",
  "sedatives",
  "bp_heart",
];

const humanize = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const pickField = (fields, name) =>
  Array.isArray(fields)
    ? fields.find((f) => (f.uniqueId ?? f.name) === name)?.value
    : fields?.[name];

const hydrate = (detail, locale) => {
  const fields = detail?.data || detail || [];
  const arr = Array.isArray(fields) ? fields : [];
  const val = (name) => pickField(arr, name) ?? detail?.[name];
  const measurements = hydrateHeightWeightFields(
    val("height") || "",
    val("weight") || "",
    locale,
  );
  return {
    step1: {
      firstname: val("first_name") || "",
      lastname: val("last_name") || "",
      testPersonId: val("user_id_from_client") || val("test_person_id") || "",
      gender: val("gender") || "",
      birthYear: val("birth_year") || "",
      birthMonth: val("birth_month") || "",
    },
    step2: {
      zipcode: val("zip_code") || "",
      email: val("email") || "",
      phone: val("mobile_phone_number") || "",
      ...measurements,
      exercises: val("fitness_level") || "",
      injuries: val("injury_or_surgery") || [],
      medications: val("medications") || [],
    },
  };
};

const isValidName = (s) => /^[\p{L}\p{M}\s'-]+$/u.test(s);
const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

export default function MmtEditTestPerson({
  readOnly = false,
  seniorUuidProp,
  onClose,
  onSuccess,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locale = getCurrentLocale();
  const text = pageLocale("testPersonForm");
  const contactRules = getContactFieldRules(locale);
  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i, 1).toLocaleString(locale, { month: "short" }),
  }));
  const seniorUuid = seniorUuidProp ?? searchParams.get("seniorUuid");
  const [session] = useState(getStoredSession);
  const [step1, setStep1] = useState({
    firstname: "",
    lastname: "",
    testPersonId: "",
    gender: "",
    birthYear: "",
    birthMonth: "",
  });
  const [step2, setStep2] = useState({
    zipcode: "",
    email: "",
    phone: "",
    height: "",
    heightFeet: "",
    heightInches: "",
    weight: "",
    exercises: "",
    injuries: [],
    medications: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!session || !seniorUuid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    getPatientDetails(session, seniorUuid)
      .then((detail) => {
        if (cancelled || !detail) return;
        const next = hydrate(detail, locale);
        setStep1(next.step1);
        setStep2(next.step2);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.message || err?.message || text.load_error,
          );
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [locale, seniorUuid, session, text.load_error]);

  const setF1 = (key, value) => setStep1((prev) => ({ ...prev, [key]: value }));
  const setF2 = (key, value) => setStep2((prev) => ({ ...prev, [key]: value }));
  const toggle = (key, value) =>
    setStep2((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((v) => v !== value)
        : [...prev[key], value],
    }));

  const back = () => {
    if (postWebViewMessage("BACK_TO_USER_LIST")) return;
    if (onClose) {
      onClose();
      return;
    }
    navigate(buildSessionPath(USER_LIST, session), { replace: true });
  };

  const done = () => {
    sessionStorage.removeItem("userList");
    if (onSuccess) {
      onSuccess();
      return;
    }
    navigate(buildSessionPath(USER_LIST, session), { replace: true });
  };

  const submit = async () => {
    if (!isFormValid || !session || !seniorUuid) return;
    setSaving(true);
    setError("");
    try {
      const measurements = {
        ...step2,
        ...toStoredHeightWeight(step2, locale),
      };
      await updatePatient(session, seniorUuid, step1, measurements);
      if (!postWebViewMessage("TEST_PERSON_UPDATED")) done();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || text.save_error);
    } finally {
      setSaving(false);
    }
  };

  if (!session && !isWebView()) return null;

  // ── Field-level validation (only fires when the field has content) ──────────
  const firstNameError =
    step1.firstname.trim() !== "" && !isValidName(step1.firstname);
  const lastNameError =
    step1.lastname.trim() !== "" && !isValidName(step1.lastname);
  const emailError =
    step2.email.trim() !== "" && !isValidEmail(step2.email.trim());
  const phoneError =
    step2.phone.trim() !== "" &&
    !contactRules.isValidPhone(digitsOnly(step2.phone));
  const zipcodeError =
    step2.zipcode.trim() !== "" &&
    !contactRules.isValidPostal(digitsOnly(step2.zipcode));
  const {
    heightError,
    weightError,
    isValid: measurementsValid,
    metric,
  } = getHeightWeightValidation(step2, locale);

  const isFormValid =
    step1.firstname.trim() !== "" &&
    !firstNameError &&
    step1.lastname.trim() !== "" &&
    !lastNameError &&
    step1.gender !== "" &&
    step1.birthYear !== "" &&
    step1.birthMonth !== "" &&
    !emailError &&
    !phoneError &&
    !zipcodeError &&
    measurementsValid;

  const disabled = readOnly || saving;

  return (
    <main className="mmt-test-person-page">
      <header className="mmt-tp-header">
        <button onClick={back} aria-label={text.back} className="mmt-tp-back">
          <img src={backArrow} alt="" />
        </button>
        <h1>{readOnly ? text.view_title : text.edit_title}</h1>
      </header>

      {loading ? (
        <section className="mmt-tp-form">
          <p>{text.loading}</p>
        </section>
      ) : (
        <section className="mmt-tp-form">
          {error ? <div className="mmt-tp-error">{error}</div> : null}

          <div className="mmt-field">
            <span>
              {text.first_name} {!readOnly && "*"}
            </span>
            <input
              value={step1.firstname}
              onChange={(e) => setF1("firstname", e.target.value)}
              maxLength={50}
              className={firstNameError ? "mmt-input-error" : ""}
              disabled={disabled}
            />
            {firstNameError ? (
              <em className="mmt-tp-hint">{text.name_hint}</em>
            ) : null}
          </div>

          <div className="mmt-field">
            <span>
              {text.last_name} {!readOnly && "*"}
            </span>
            <input
              value={step1.lastname}
              onChange={(e) => setF1("lastname", e.target.value)}
              maxLength={50}
              className={lastNameError ? "mmt-input-error" : ""}
              disabled={disabled}
            />
            {lastNameError ? (
              <em className="mmt-tp-hint">{text.name_hint}</em>
            ) : null}
          </div>

          <div className="mmt-field">
            <span>{text.person_id}</span>
            <input value={step1.testPersonId} disabled />
          </div>

          <div className="mmt-field">
            <span>
              {text.gender} {!readOnly && "*"}
            </span>
            <div className="mmt-tp-radio-group">
              {GENDERS.map((g) => (
                <button
                  key={g}
                  type="button"
                  className={`mmt-tp-radio-btn${step1.gender === g ? " selected" : ""}`}
                  onClick={() => setF1("gender", g)}
                  disabled={disabled}
                >
                  {text[g]}
                </button>
              ))}
            </div>
          </div>

          <div className="mmt-tp-row">
            <label className="mmt-field">
              <span>
                {text.birth_year} {!readOnly && "*"}
              </span>
              <select
                value={step1.birthYear}
                onChange={(e) =>
                  setF1(
                    "birthYear",
                    e.target.value ? Number(e.target.value) : "",
                  )
                }
                disabled={disabled}
              >
                <option value="">{text.year}</option>
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>

            <label className="mmt-field">
              <span>
                {text.birth_month} {!readOnly && "*"}
              </span>
              <select
                value={step1.birthMonth}
                onChange={(e) =>
                  setF1(
                    "birthMonth",
                    e.target.value ? Number(e.target.value) : "",
                  )
                }
                disabled={disabled}
              >
                <option value="">{text.month}</option>
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mmt-tp-section-divider" />

          <div className="mmt-field">
            <span>{text.zip_code}</span>
            <input
              value={step2.zipcode}
              onChange={(e) =>
                setF2(
                  "zipcode",
                  sanitizeContactValue(
                    e.target.value,
                    contactRules.postalMaxLength,
                  ),
                )
              }
              inputMode="numeric"
              maxLength={contactRules.postalMaxLength}
              className={zipcodeError ? "mmt-input-error" : ""}
              disabled={disabled}
            />
            {zipcodeError ? (
              <em className="mmt-tp-hint">{text.zip_hint}</em>
            ) : null}
          </div>

          <div className="mmt-field">
            <span>{text.email}</span>
            <input
              type="email"
              inputMode="email"
              value={step2.email}
              onChange={(e) => setF2("email", e.target.value)}
              className={emailError ? "mmt-input-error" : ""}
              disabled={disabled}
            />
            {emailError ? (
              <em className="mmt-tp-hint">{text.email_hint}</em>
            ) : null}
          </div>

          <div className="mmt-field">
            <span>{text.phone_number}</span>
            <input
              type="tel"
              inputMode="numeric"
              value={step2.phone}
              onChange={(e) =>
                setF2(
                  "phone",
                  sanitizeContactValue(
                    e.target.value,
                    contactRules.phoneMaxLength,
                  ),
                )
              }
              maxLength={contactRules.phoneMaxLength}
              className={phoneError ? "mmt-input-error" : ""}
              disabled={disabled}
            />
            {phoneError ? (
              <em className="mmt-tp-hint">{text.phone_hint}</em>
            ) : null}
          </div>

          <HeightWeightFields
            values={step2}
            onChange={setF2}
            heightError={heightError}
            weightError={weightError}
            disabled={disabled}
            metric={metric}
            text={text}
            required={!readOnly}
            autoFocusHeight={searchParams.get("focus") === "height"}
          />

          <div className="mmt-field">
            <span>{text.exercise_frequency}</span>
            <div className="mmt-tp-radio-group">
              {EXERCISES.map((e) => (
                <button
                  key={e}
                  type="button"
                  className={`mmt-tp-radio-btn${step2.exercises === e ? " selected" : ""}`}
                  onClick={() => setF2("exercises", e)}
                  disabled={disabled}
                >
                  {text[`exercise_${e}`]}
                </button>
              ))}
            </div>
          </div>

          <div className="mmt-field">
            <span>{text.injuries_surgeries}</span>
            <div className="mmt-tp-chip-group">
              {INJURIES.map((i) => (
                <button
                  key={i}
                  type="button"
                  className={`mmt-tp-chip${step2.injuries.includes(i) ? " selected" : ""}`}
                  onClick={() => toggle("injuries", i)}
                  disabled={disabled}
                >
                  {text[`injury_${i}`] || humanize(i)}
                </button>
              ))}
            </div>
          </div>

          <div className="mmt-field">
            <span>{text.medications}</span>
            <div className="mmt-tp-chip-group">
              {MEDICATIONS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`mmt-tp-chip${step2.medications.includes(m) ? " selected" : ""}`}
                  onClick={() => toggle("medications", m)}
                  disabled={disabled}
                >
                  {text[`med_${m}`] || humanize(m)}
                </button>
              ))}
            </div>
          </div>

          {!readOnly ? (
            <div className="mmt-tp-actions">
              <button
                type="button"
                className="mmt-primary-button"
                onClick={submit}
                disabled={!isFormValid || saving}
              >
                {saving ? text.saving : text.save}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </main>
  );
}
